import React, {
  useEffect,
  useRef,
  useState,
} from 'react';
import { useRouter } from 'next/router';

import { StoppedPageComponent } from '@/core/components/StoppedPageComponent';
import { MainMedia } from '@/core/components/MainMedia';
import {
  ActionCenterTypes,
  ActionResponseStatus,
  ContactInfo,
  FormSubjectMessagePair,
  MatchedTargets,
  MatchTargetsPayload,
  MessagePairTypes,
  QueryParams,
  SubjectMessagePair,
  SubmitResponse,
  TakeActionErrorsFields,
  TakeActionFormPayload,
  TakeActionFormState,
  TakeActionLandingPageProps,
  Targets,
} from '@/core/interfaces/common';

import { TakeActionFormContainer } from '@/features/NewForm/TakeActionFormContainerRedux';

import {
  ConversionDetails,
  googleAnalyticsPageview,
  initGoogleTagManager,
  metaPixelTrackLead,
  trackCustomerConversion,
} from '@/utils/analytics';
import {
  getAnalyticsSessionId,
  trackFormSubmit,
} from '@/utils/beacon';
import { request } from '@/utils/request';
import {
  getPrefilledContactUuid,
  handleCreateCustomFieldsParams,
  handleNumberFormat,
  scrollToTop,
  transformPrefix,
  updateFormStateFromQueryParams,
} from '@/utils/helpers';
import {
  encryptedPrefillParam,
  readEncryptedPrefill,
} from '@/utils/encryptedPrefill';
import { getSubmitOutcome } from '@/utils/submitOutcome';
import { useResponsiveMaxLength } from '@/utils/hooks/useResponsiveMaxLength';
import { useFormValidationFocus } from '@/utils/useFormValidationFocus';

import {
  GeneralErrorMessage,
  LogoLink,
  TextAreaDescription,
} from '@/themes/globalStyles';

import { ActionTakenComponent } from '../FormsDesignV4/components/ActionTakenComponent';
import { AdvocatesCounter } from '../FormsDesignV4/components/AdvocatesCounter';
import { LandingPageFooter } from '../FormsDesignV4/components/LandingPageFooter';
import { ShowMoreText } from '../FormsDesignV4/components/ShowMoreText';
import { ShowMoreHtml } from '../FormsDesignV4/components/ShowMoreHtml';
import {
  FormSection,
  FormWrapper,
  Header,
  LandingPageWrapper,
  Logo,
  LogoWrapper,
  PickActionTypeWrapper,
  ResetForm,
  ResetFormButton,
  StyledPickActionType,
  StyledTextDescription,
  StyledWhyTakeActionWrapper,
  Title,
} from '../FormsDesignV4/FormsDesignV4.styles';

export interface CustomFieldErrors {
  value: Array<string>;
}

const defaultBackgroundColor = '#2979FF';

// Build an editable message pair from a prefilled message, deep-copying the
// nested subject/body so form edits never mutate the action center response
// objects these are derived from.
const toFormSubjectMessagePair = (
  prefilledMessage: SubjectMessagePair
): FormSubjectMessagePair => ({
  ...prefilledMessage,
  body: prefilledMessage.body.map(block => ({ ...block })),
  personalMessage: '',
  subject: { ...prefilledMessage.subject },
});

export const TakeActionLandingPageV4 = ({
  actionCenterDetails,
  contactInfo,
  nonGeocodedTargets,
}: TakeActionLandingPageProps) => {
  const router = useRouter();
  const {
    debugThankYou,
    uuid,
  } = router.query;

  // The query as the page loaded with it. Prefill is applied from this once;
  // later router updates must not re-apply it over what the visitor has
  // typed since.
  const [initialQuery] = useState(router.query);
  const { [encryptedPrefillParam]: prefillToken } = initialQuery;

  // The mus_* params to prefill the form from: plaintext query params, or
  // those decrypted from mus_data (see below). Unset while decrypting.
  const [
    queryPrefill,
    setQueryPrefill,
  ] = useState<QueryParams | null>(prefillToken ? null : initialQuery);

  const {
    customFields,
    emailChannel,
    facebookPixelEnabled,
    facebookPixelId,
    headlineConfig,
    isGeocoded,
    googleAnalyticsEnabled,
    googleAnalyticsId,
    landingPageImage,
    nextSteps,
    logo,
    mode,
    optIn,
    messagingType,
    organization,
    phoneChannel,
    prefilledMessages,
    schema,
    status,
    socialShareButtonEnabled,
    showAdvocatesCount,
    talkingPoints,
    thankYouMessage,
  } = actionCenterDetails;

  const {
    youtubeId,
    vimeoCode,
  } = headlineConfig;
  const {
    emailType,
    phoneType,
  } = ActionCenterTypes;
  const {
    messageRotation,
    messageMapping,
  } = MessagePairTypes;

  const [
    actionCenterType,
    setActionCenterType,
  ] = useState<ActionCenterTypes>(emailType);
  const [
    formState,
    setFormState,
  ] = useState<TakeActionFormState>({
    customFields: [],
    email: '',
    firstName: '',
    isOptInEmail: true,
    isOptInText: true,
    lastName: '',
    phoneNumber: '',
    prefix: '',
    streetAddress: '',
    subjectMessagePairs: [
      {
        body: [
          {
            isAllowEdit: false,
            name: '',
          },
        ],
        isAllowPersonalMessage: false,
        personalMessage: '',
        subject: {
          isAllowEdit: false,
          name: '',
        },
        uuid: '',
      },
    ],
    zipCode: '',
  });
  const [
    isNewContact,
    setIsNewContact,
  ] = useState(false);
  const [
    actionMode,
    setActionMode,
  ] = useState(ActionResponseStatus.live);
  const [
    isActionTaken,
    setIsActionTaken,
  ] = useState(debugThankYou === '1');
  const [
    targets,
    setTargets,
  ] = useState<Targets>([]);
  const [
    isPending,
    setIsPending,
  ] = useState(false);
  const [
    error,
    setError,
  ] = useState<TakeActionErrorsFields>({});
  const [
    previewTargets,
    setPreviewTargets,
  ] = useState<MatchedTargets>([]);
  const [
    currentMessageIndex,
    setCurrentMessageIndex,
  ] = useState<number>(0);

  const maxLength = useResponsiveMaxLength();
  const formRef = useRef<HTMLFormElement>(null);

  useFormValidationFocus(formRef);

  useEffect(() => {
    if (!emailChannel && phoneChannel) {
      setActionCenterType(phoneType);
    }
  }, [
    emailChannel,
    phoneChannel,
    phoneType,
  ]);

  useEffect(() => {
    let subjectMessagePairs: Array<FormSubjectMessagePair> = [];

    if (prefilledMessages) {
      if (messagingType === messageMapping) {
        if (isGeocoded) {
          subjectMessagePairs = [toFormSubjectMessagePair(prefilledMessages[0])];
        } else {
          subjectMessagePairs = prefilledMessages.map(toFormSubjectMessagePair);
        }
      } else if (messagingType === messageRotation) {
        subjectMessagePairs = [
          toFormSubjectMessagePair(prefilledMessages[Math.floor(
            Math.random() * prefilledMessages.length
          )]),
        ];
      }
    }

    const formCustomFields = customFields.map(customField => ({
      ...customField,
      value: contactInfo?.customFields.find(customFieldAnswer => customFieldAnswer.customField === customField.uuid)?.value || '',
    }));

    if (!isGeocoded && nonGeocodedTargets?.length) {
      setPreviewTargets(nonGeocodedTargets);
    }

    setFormState(prevValues => ({
      ...prevValues,
      customFields: formCustomFields,
      subjectMessagePairs,
    }));

    if (contactInfo) {
      const prefix = transformPrefix(contactInfo.prefix);

      setFormState(prevValues => ({
        ...prevValues,
        email: contactInfo.email,
        firstName: contactInfo.firstName,
        lastName: contactInfo.lastName,
        phoneNumber: handleNumberFormat(contactInfo.phoneNumber) || '',
        prefix,
        streetAddress: contactInfo.streetAddress,
        zipCode: contactInfo.zipCode ? contactInfo.zipCode.substring(0, 5) : '',
      }));

      const emptyFieldsError = Object.keys(contactInfo).reduce<TakeActionErrorsFields>(
        (errorFieldsAcc, key) => {
          if (key === 'customFields') {
            return errorFieldsAcc;
          }

          if (!contactInfo[key as keyof ContactInfo]) {
            return {
              ...errorFieldsAcc,
              [key]: ['This field may not be blank.'],
            };
          }

          return {
            ...errorFieldsAcc,
          };
        },
        {}
      );

      emptyFieldsError.customFields = formCustomFields.map(formCustomField => ({
        value: formCustomField.value ? [] : ['This field may not be blank.'],
      }));

      setError(prevValue => ({
        ...prevValue,
        ...emptyFieldsError,
      }));
    }
  }, [
    contactInfo,
    customFields,
    isGeocoded,
    prefilledMessages,
    messageMapping,
    messageRotation,
    messagingType,
  ]);

  // Decrypt mus_data, if present. A valid token replaces the plaintext mus_*
  // params entirely (no merging); an invalid or expired one falls back to
  // them. Nothing is applied while decrypting, so the two never mix.
  useEffect(() => {
    if (!prefillToken) {
      return undefined;
    }

    let isCurrent = true;

    readEncryptedPrefill(prefillToken).then(decrypted => {
      if (isCurrent) {
        setQueryPrefill(decrypted || initialQuery);
      }
    });

    return () => {
      isCurrent = false;
    };
  }, [
    initialQuery,
    prefillToken,
  ]);

  // Conditionally populate form with contact data from query parameters.
  // A contact record (contactInfo, above) takes precedence. Use the functional
  // form of setFormState to avoid stale closure issues where
  // formState.subjectMessagePairs would not reflect the messages set above.
  useEffect(() => {
    if (contactInfo || !queryPrefill) {
      return;
    }

    setFormState(prevValues => {
      const updatedState = updateFormStateFromQueryParams(queryPrefill, prevValues);

      return updatedState ?
        {
          ...prevValues,
          ...updatedState,
        } :
        prevValues;
    });
  }, [
    contactInfo,
    queryPrefill,
  ]);

  const pageBackgroundColor = headlineConfig?.backgroundColor || defaultBackgroundColor;

  useEffect(() => {
    if (facebookPixelId && facebookPixelEnabled) {
      import('react-facebook-pixel')
        .then(module => module.default)
        .then(ReactPixel => {
          ReactPixel.init(facebookPixelId, undefined, {
            autoConfig: true,
            debug: false,
          });
          ReactPixel.pageView(); // For tracking page view
        });
    }
  }, [
    facebookPixelId,
    facebookPixelEnabled,
  ]);

  useEffect(() => {
    if (googleAnalyticsId) {
      if (googleAnalyticsId.startsWith('GTM-')) {
        initGoogleTagManager(googleAnalyticsId);
      } else {
        googleAnalyticsPageview(googleAnalyticsId);
      }
    }
  }, [googleAnalyticsId]);

  const handleResetForm = () => {
    setIsNewContact(true);

    setFormState(prevValues => ({
      ...prevValues,
      customFields: prevValues.customFields.map(customField => ({
        ...customField,
        value: '',
      })),
      email: '',
      firstName: '',
      lastName: '',
      phoneNumber: '',
      prefix: '',
      streetAddress: '',
      zipCode: '',
    }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setIsPending(true);

    const params: TakeActionFormPayload = {
      action: actionCenterType,
      contact: {
        email: formState.email,
        firstName: formState.firstName,
        isOptInEmail: formState.isOptInEmail,
        isOptInText: formState.isOptInText,
        lastName: formState.lastName,
        phoneNumber: formState.phoneNumber,
        prefix: formState.prefix,
        streetAddress: formState.streetAddress,
        zipCode: formState.zipCode,
        ...getPrefilledContactUuid(contactInfo, formState, isNewContact),
      },
      sessionId: getAnalyticsSessionId(),
      ...(formState.customFields ?
        { customFields: handleCreateCustomFieldsParams(formState.customFields) } :
        {}),
      meta: {
        params: window.location.search,
        source: window.location.href,
        type: 'landing-v4',
      },
    };

    if (actionCenterType === emailType) {
      if (messagingType === messageMapping) {
        params.messages = formState.subjectMessagePairs.map(({
          body,
          personalMessage,
          uuid: subjectMessagePairUuid,
          subject,
        }) => {
          const message = body.map(messageData => messageData.name) || [];

          return ({
            message: [
              ...(personalMessage ? [personalMessage] : []),
              ...message,
            ].join('\n\n'),
            subject: subject.name,
            targets: previewTargets
              .filter(target => target.mappedMessage === subjectMessagePairUuid)
              .map(target => target.uuid),
            uuid: subjectMessagePairUuid,
          });
        });
      } else if (messagingType === messageRotation) {
        const message = formState.subjectMessagePairs[0] ?
          formState.subjectMessagePairs[0].body.map(
            messageData => messageData.name
          ) :
          [];

        params.message = [
          ...(formState.subjectMessagePairs[0]?.personalMessage ?
            [formState.subjectMessagePairs[0].personalMessage] :
            []
          ),
          ...message,
        ].join('\n\n');
        params.messageId = formState.subjectMessagePairs[0].uuid;
        params.subject = formState.subjectMessagePairs[0].subject.name;
      }
    }

    return request
      .post<SubmitResponse>(`/public/act/${uuid}/${actionCenterType === emailType ? 'send_message' : 'make_phone_call'}/`, params)
      .then(({ data }) => {
        setTargets(data.targets || []);

        if (data.status === 'error') {
          setError({
            submitError: data.message,
          });
        } else {
          trackFormSubmit({
            organization: organization.uuid,
            version: 'v4',
          });
        }

        if (data.status === ActionResponseStatus.test) {
          setActionMode(ActionResponseStatus.test);
        } else if (data.status === ActionResponseStatus.petition) {
          setActionMode(ActionResponseStatus.petition);
        }
        setIsActionTaken(true);

        const conversion: ConversionDetails = {
          actionType: mode === 'petition' ? 'petition' : actionCenterType,
          formType: 'action_center',
          outcome: getSubmitOutcome(data.status),
        };

        metaPixelTrackLead(facebookPixelId, facebookPixelEnabled, conversion);
        trackCustomerConversion(googleAnalyticsId, googleAnalyticsEnabled, conversion);

        return data;
      })
      .catch(({ response: { data = {} } = {} }) => {
        if (data?.status === 'error') {
          setError({
            generalError: data.message,
          });
        } else {
          const messages = data.messages ?
            data.messages :
            [
              {
                message: data.message,
                subject: data.subject,
              },
            ];

          if (messages[0].message || messages[0].subject) {
            setCurrentMessageIndex(0);
          }

          setError({
            ...(data.customFields ? { customFields: data.customFields } : {}),
            ...data.contact,
            messages,
          });
        }
      })
      .finally(() => {
        setIsPending(false);
        scrollToTop();
      });
  };

  const handleFetchPreviewTargets = (
    streetAddress: string | undefined,
    zipCode: string | undefined
  ) => {
    const contactStreetAddress = streetAddress || formState.streetAddress;
    const contactZipCode = zipCode || formState.zipCode;

    if (contactStreetAddress && contactZipCode && isGeocoded) {
      const params: MatchTargetsPayload = {
        streetAddress: contactStreetAddress,
        zipCode: contactZipCode,
      };

      request
        .get<MatchedTargets, MatchTargetsPayload>(`/public/act/${uuid}/match_targets/`, { params })
        .then(({ data }) => {
          setPreviewTargets(data || []);

          if (messagingType === messageMapping && data) {
            const filteredMessages = prefilledMessages?.filter(
              ({ uuid: messageUuid }) => data.some(target => target.mappedMessage === messageUuid)
            ) || [];

            setFormState(prevValues => ({
              ...prevValues,
              subjectMessagePairs: filteredMessages.map(filteredMessage => ({
                ...filteredMessage,
                personalMessage: '',
              })),
            }));
          }
        });
    }
  };

  useEffect(() => {
    if (contactInfo?.streetAddress && contactInfo?.zipCode && isGeocoded) {
      handleFetchPreviewTargets(contactInfo.streetAddress, contactInfo.zipCode);
    }
  }, []);

  if (status === 'stopped') {
    return (
      <StoppedPageComponent
        backgroundColor={pageBackgroundColor}
        textColor={headlineConfig?.textColor || '#000'}
        title="This Action Center has been stopped."
      />
    );
  }

  const websiteLink = organization.websiteUrl?.startsWith('http://') || organization.websiteUrl?.startsWith('https://') ? organization.websiteUrl : `https://${organization.websiteUrl}`;

  const headerSection = (
    <Header
      backgroundcolor={pageBackgroundColor}
      isActionTaken={isActionTaken}
      textcolor={headlineConfig?.textColor || '#000'}
    >
      <LogoWrapper>
        <LogoLink
          aria-label={organization.websiteUrl ? `${organization.name} website` : undefined}
          href={organization.websiteUrl ? websiteLink : undefined}
          target="_blank"
          rel="noopener noreferrer"
        >
          {logo ?
            (
              <Logo
                alt=""
                role="presentation"
                src={logo}
                title={`${organization.name} logo`}
                // src="https://i.imgur.com/ur4MH1P.jpeg"
              />
            ) :
            organization.name
          }
        </LogoLink>
      </LogoWrapper>
    </Header>
  );

  const footerSection = (
    <LandingPageFooter
      backgroundColor={pageBackgroundColor}
      organization={organization}
      shareMessage="Help spread the word and share this campaign"
      shareTitle={headlineConfig?.headlineText}
      socialShareButtonEnabled={socialShareButtonEnabled}
      textColor={headlineConfig?.textColor}
      uuid={uuid}
    />
  );

  return (
    <LandingPageWrapper
      backgroundcolor={isActionTaken ? pageBackgroundColor : 'white'}
      className={schema?.cssClass}
      isActionTaken={isActionTaken}
    >
      {headerSection}
      {isActionTaken &&
        (
          <ActionTakenComponent
            actionCenterType={actionCenterType}
            actionMode={actionMode}
            backgroundColor={pageBackgroundColor}
            buttonColor={headlineConfig?.buttonColor || defaultBackgroundColor}
            nextSteps={nextSteps}
            isFullScreen
            multipleMessages={formState.subjectMessagePairs.length > 1}
            socialShareButtonEnabled={socialShareButtonEnabled}
            submitError={error?.submitError}
            talkingPoints={talkingPoints}
            targets={targets}
            textColor={headlineConfig?.textColor || '#000'}
            thankYouMessage={thankYouMessage}
            uuid={uuid}
          />
        )}
      {!isActionTaken && (
        <>
          <FormWrapper>
            <section>
              <StyledWhyTakeActionWrapper>
                <MainMedia
                  vimeoCode={vimeoCode}
                  youtubeId={youtubeId}
                  mainImage={landingPageImage}
                />
                <Title>
                  {headlineConfig?.headlineText}
                </Title>

                {/* The advocates count is dependent on a secondary API call! */}
                {/* The component will make this API call when it is called, but we do NOT */}
                {/* want to make this unless actually necessary (it will slow page load). */}
                {showAdvocatesCount && (
                  <AdvocatesCounter
                    backgroundColor={pageBackgroundColor}
                    uuid={uuid}
                  />
                )}
                {headlineConfig?.descriptionBody ?
                  (
                    <ShowMoreHtml
                      htmlWrapper={StyledTextDescription}
                      html={headlineConfig?.descriptionBody}
                      maxLength={maxLength}
                    />
                  ) :
                  (
                    <ShowMoreText
                      textWrapper={TextAreaDescription}
                      text={headlineConfig?.textArea}
                      maxLength={maxLength}
                    />
                  )}
              </StyledWhyTakeActionWrapper>
            </section>
            <FormSection
              backgroundcolor={pageBackgroundColor}
            >
              {error?.generalError && (
                <GeneralErrorMessage>{error?.generalError}</GeneralErrorMessage>
              )}
              {emailChannel && phoneChannel && (
                <PickActionTypeWrapper>
                  <StyledPickActionType
                    actionCenterType={actionCenterType}
                    backgroundColor={pageBackgroundColor}
                    setActionCenterType={setActionCenterType}
                    textColor={headlineConfig?.textColor || '#000'}
                  />
                </PickActionTypeWrapper>
              )}
              {!!contactInfo && !isNewContact && (
                <ResetFormButton onClick={handleResetForm}>
                  <span>
                    {`Not ${contactInfo?.firstName} ${contactInfo?.lastName}? `}
                  </span>
                  <ResetForm textcolor={pageBackgroundColor}>
                    Reset Form
                  </ResetForm>
                </ResetFormButton>
              )}
              <form
                onSubmit={handleSubmit}
                ref={formRef}
              >
                <TakeActionFormContainer
                  actionCenterType={actionCenterType}
                  backgroundColor={pageBackgroundColor}
                  buttonColor={headlineConfig?.buttonColor || defaultBackgroundColor}
                  currentMessageIndex={currentMessageIndex}
                  emailChannel={emailChannel}
                  error={error}
                  formState={formState}
                  handleBlurAddress={handleFetchPreviewTargets}
                  hideMessageHeadline
                  isDisabledInputText
                  isPending={isPending}
                  mode={mode}
                  optIn={optIn}
                  organizationName={organization.name}
                  phoneChannel={phoneChannel}
                  previewTargets={(
                    messagingType === messageMapping &&
                    actionCenterType === emailType
                  ) ?
                    // eslint-disable-next-line max-len
                    previewTargets.filter(({ mappedMessage }) => mappedMessage === formState.subjectMessagePairs[currentMessageIndex]?.uuid) :
                    previewTargets
                  }
                  setCurrentMessageIndex={setCurrentMessageIndex}
                  setError={setError}
                  setFormState={setFormState}
                  talkingPoints={talkingPoints}
                  textColor={headlineConfig?.textColor || '#000'}
                />
              </form>
            </FormSection>
          </FormWrapper>
          {footerSection}
        </>
      )}
    </LandingPageWrapper>
  );
};
